import { makeAutoObservable, runInAction, reaction } from "mobx";
import { RootStore } from "@intrasync/stores";
import { PDFDocument, rgb } from "pdf-lib";
import * as fabric from "fabric";
import debounce from "lodash.debounce";

export class PdfEditorViewStore {
  // UI State only - no entities stored here
  selectedAnnotationId: string | null = null;
  loadedDocument: any = null; // Store the loaded document data
  totalPages = 0;
  zoomLevel = 100;
  selectedTool: "select" | "highlight" | "text" | "drawing" | "eraser" =
    "select";
  isLoading = false;
  error: string | null = null;
  pdfDoc: PDFDocument | null = null;
  fabricCanvas: fabric.Canvas | null = null;
  pageCache: Map<number, string> = new Map(); // Cache rendered pages

  // Collaboration state
  isCollaborative = false;
  localAnnotationQueue: any[] = [];
  currentEtag: string | null = null;
  pendingSyncCount = 0;
  lastSyncTime: Date | null = null;
  syncError: string | null = null;

  rootStore: RootStore;

  // Debounced sync function
  debouncedSync: ReturnType<typeof debounce>;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
      pdfDoc: false,
      fabricCanvas: false,
      pageCache: false,
      debouncedSync: false,
    });
    this.rootStore = rootStore;

    // Initialize debounced sync with 500ms delay
    this.debouncedSync = debounce(this.executeBatchSync.bind(this), 500);

    // Setup Socket.io event handlers for collaboration
    this.setupSocketHandlers();

    // Setup reaction to load document when documentId changes
    reaction(
      () => this.selectedDocumentId,
      (documentId) => {
        if (documentId && !this.isLoading) {
          console.log("Document ID changed, loading document:", documentId);
          // Small delay to ensure all route params are set
          setTimeout(() => {
            this.loadDocument();
          }, 100);
        }
      },
      { fireImmediately: true, delay: 100 } // Fire immediately with delay if documentId is already set
    );
  }

  // Actions - UI state only
  setPdfDoc = (doc: PDFDocument | null) => {
    this.pdfDoc = doc;
  };

  setFabricCanvas = (canvas: fabric.Canvas | null) => {
    this.fabricCanvas = canvas;
    console.log("fabricCanvas initialized:", canvas);

    // Load annotations for current page when canvas is set
    if (canvas) {
      console.log("Loading annotations for page:", this.currentPage);
      // Small delay to ensure canvas is fully ready
      setTimeout(() => {
        this.loadAnnotationsForPage(this.currentPage);
      }, 100);
    }
  };


  setSelectedAnnotation = (id: string | null) => {
    this.selectedAnnotationId = id;
  };

  setCurrentPage = (page: number) => {
    this.saveCurrentPageAnnotations();
    // Preserve the current route params (documentId and tab)
    const currentParams = this.rootStore.routerStore.params;
    this.rootStore.routerStore.goTo(null, currentParams, { pageNumber: page });
    this.loadAnnotationsForPage(page);
  };

  setTotalPages = (pages: number) => {
    this.totalPages = pages;
  };

  setZoomLevel = (level: number) => {
    this.zoomLevel = level;
    if (this.fabricCanvas) {
      this.fabricCanvas.setZoom(level / 100);
      this.fabricCanvas.setDimensions({
        width: (this.fabricCanvas.width || 0) * (level / 100),
        height: (this.fabricCanvas.height || 0) * (level / 100),
      });
    }
  };

  setSelectedTool = (tool: typeof this.selectedTool) => {
    console.log("setSelectedTool", tool);
    console.log("Previous Tool:", this.selectedTool);
    this.selectedTool = tool;
    this.updateCanvasMode();
  };

  updateCanvasMode = () => {
    if (!this.fabricCanvas) return;

    // Reset canvas interaction modes
    this.fabricCanvas.isDrawingMode = false;
    this.fabricCanvas.selection = false;

    switch (this.selectedTool) {
      case "select":
        this.fabricCanvas.selection = true;
        this.fabricCanvas.forEachObject((obj) => {
          obj.selectable = true;
          obj.evented = true;
        });
        break;

      case "drawing":
        this.fabricCanvas.isDrawingMode = true;
        this.fabricCanvas.freeDrawingBrush = new fabric.PencilBrush(
          this.fabricCanvas
        );
        this.fabricCanvas.freeDrawingBrush.width = 2;
        this.fabricCanvas.freeDrawingBrush.color = "#ff0000";
        break;

      case "eraser":
        this.fabricCanvas.isDrawingMode = true;
        // Note: EraserBrush might not be available in all fabric versions
        // Fallback to regular brush with white color for erasing effect
        this.fabricCanvas.freeDrawingBrush = new fabric.PencilBrush(
          this.fabricCanvas
        );
        this.fabricCanvas.freeDrawingBrush.width = 10;
        this.fabricCanvas.freeDrawingBrush.color = "#ffffff";
        break;

      case "text":
      case "highlight":
        // These are handled by mouse events
        // Enable selection but disable existing object interaction
        this.fabricCanvas.selection = false;
        this.fabricCanvas.forEachObject((obj) => {
          obj.selectable = false;
          obj.evented = false;
        });
        // Ensure canvas can receive mouse events
        this.fabricCanvas.renderAll();
        break;
    }
  };

  // Async Actions
  loadDocument = async () => {
    const documentId = this.selectedDocumentId;
    if (!documentId) {
      console.log("No document ID to load");
      this.error = "No document ID provided";
      return;
    }

    // Prevent duplicate loading
    if (this.isLoading) {
      console.log("Already loading document");
      return;
    }

    console.log("Starting to load document:", documentId);
    this.isLoading = true;
    this.error = null;
    this.loadedDocument = null; // Clear any previously loaded document

    try {
      // Ensure service stores exist
      if (!(this.rootStore as any).pdfDocumentsStore) {
        throw new Error("pdfDocumentsStore not initialized");
      }
      if (!(this.rootStore as any).pdfAnnotationsStore) {
        throw new Error("pdfAnnotationsStore not initialized");
      }

      // First try to find in existing entities
      let doc = (this.rootStore as any).pdfDocumentsStore.entities.find((d: any) => d.id === documentId);
      
      // If not in entities, fetch it
      if (!doc) {
        doc = await (this.rootStore as any).pdfDocumentsStore.get(documentId);
      }
      
      if (!doc) {
        throw new Error(`Document ${documentId} not found`);
      }
      
      // Store the document reference
      this.loadedDocument = doc;

      await (this.rootStore as any).pdfAnnotationsStore.find({
        query: { documentId: parseInt(documentId as string, 10) },
      });

      // Load PDF from Azure blob URL or fallback to regular URL
      const pdfUrl = doc.blobUrl || doc.url;
      console.log("PDF URL:", pdfUrl);

      if (pdfUrl) {
        const response = await fetch(pdfUrl);
        const arrayBuffer = await response.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);

        runInAction(() => {
          this.pdfDoc = pdfDoc;
          this.totalPages = pdfDoc.getPageCount();
          this.isLoading = false;
          console.log("PDF loaded successfully. Total pages:", this.totalPages);
          console.log("Loaded document stored:", this.loadedDocument);
        });

        // Fetch annotations for this document
        if (this.selectedDocumentId) {
          console.log("Fetching annotations for document:", this.selectedDocumentId);
          await (this.rootStore as any).pdfAnnotationsStore.find({
            query: {
              documentId: parseInt(this.selectedDocumentId as string, 10),
              $limit: 1000 // Get all annotations for the document
            }
          });
        }
      } else if (doc.fileData) {
        // Fallback to base64 data if no URL available
        const byteCharacters = atob(doc.fileData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const pdfDoc = await PDFDocument.load(byteArray);

        runInAction(() => {
          this.pdfDoc = pdfDoc;
          this.totalPages = pdfDoc.getPageCount();
          this.isLoading = false;
          console.log("PDF loaded from base64. Total pages:", this.totalPages);
          console.log("Loaded document stored:", this.loadedDocument);
        });

        // Fetch annotations for this document
        if (this.selectedDocumentId) {
          console.log("Fetching annotations for document (base64):", this.selectedDocumentId);
          await (this.rootStore as any).pdfAnnotationsStore.find({
            query: {
              documentId: parseInt(this.selectedDocumentId as string, 10),
              $limit: 1000 // Get all annotations for the document
            }
          });
        }
      } else {
        throw new Error("No PDF source available for this document");
      }
    } catch (error: any) {
      console.error("Error loading document:", error);
      runInAction(() => {
        this.error = error.message;
        this.isLoading = false;
      });
    }
  };

  saveAnnotation = async (annotationData: any) => {
    try {
      if (!this.selectedDocumentId) {
        throw new Error("No document ID available for saving annotation");
      }
      
      // Convert fabric object to serializable format
      const fabricObject = annotationData.fabricObject;
      const serialized = fabricObject
        ? fabricObject.toJSON(["id", "selectable"])
        : null;
      
      // Map annotation types to valid API values
      const validTypes = ['highlight', 'text', 'drawing', 'sticky', 'underline', 'strikeout'];
      let annotationType = annotationData.type;
      
      // Map fabric types to valid annotation types
      if (annotationType === 'path' || annotationType === 'custom') {
        annotationType = 'drawing';
      } else if (annotationType === 'rect') {
        annotationType = 'highlight';
      } else if (annotationType === 'i-text' || annotationType === 'textbox') {
        annotationType = 'text';
      }
      
      // Fallback to 'drawing' if type is not valid
      if (!validTypes.includes(annotationType)) {
        annotationType = 'drawing';
      }
      
      const newAnnotation = {
        documentId: parseInt(this.selectedDocumentId as string, 10), // Convert to number
        pageNumber: annotationData.pageNumber || this.currentPage,
        type: annotationType,
        contentJson: JSON.stringify({
          ...annotationData.contentJson,
          fabricData: serialized,
        }),
      };
      console.log("new annotation", newAnnotation);
      const createdAnnotation = await (this.rootStore as any).pdfAnnotationsStore.create(newAnnotation);

      // If we have a fabric object and it was just created, update its data with the annotation ID
      if (fabricObject && createdAnnotation) {
        fabricObject.data = {
          ...fabricObject.data,
          saved: true,
          annotationId: createdAnnotation.id
        };
      }

      // Reload annotations to show the newly created one
      setTimeout(() => {
        this.loadAnnotationsForPage(this.currentPage);
      }, 100);
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message;
      });
    }
  };

  deleteAnnotation = async (annotationId: string) => {
    try {
      await (this.rootStore as any).pdfAnnotationsStore.remove(annotationId);
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message;
      });
    }
  };

  updateAnnotation = async (annotationId: string, updates: any) => {
    try {
      console.log('Updating annotation:', annotationId, 'with:', updates);

      // Prepare the update data
      const updateData: any = {
        ...updates
      };

      // If contentJson is an object, update the fabricData with new properties
      if (updateData.contentJson && typeof updateData.contentJson === 'object') {
        // Find the existing fabric object on canvas if it exists
        const fabricObject = this.fabricCanvas?.getObjects().find(
          (obj: any) => obj.data?.annotationId === annotationId
        );

        if (fabricObject) {
          // Update fabric object properties based on type
          if (updates.type === 'text' && fabricObject.type === 'i-text') {
            fabricObject.set({
              text: updates.contentJson.text,
              fill: updates.contentJson.color,
              fontSize: updates.contentJson.fontSize
            });
          } else if (updates.type === 'highlight' && fabricObject.type === 'rect') {
            fabricObject.set({
              fill: updates.contentJson.color,
              opacity: updates.contentJson.opacity || 0.3
            });
          } else if (updates.type === 'drawing' && fabricObject.type === 'path') {
            console.log('Updating drawing color to:', updates.contentJson.color, 'width:', updates.contentJson.width);
            fabricObject.set({
              stroke: updates.contentJson.color,
              strokeWidth: updates.contentJson.width || 2
            });
          }

          // Update the fabric data in contentJson with the new properties
          updateData.contentJson.fabricData = fabricObject.toJSON(['id', 'selectable', 'stroke', 'strokeWidth', 'fill']);
          this.fabricCanvas?.renderAll();
        } else if (updateData.contentJson.fabricData) {
          // If object not on current canvas, update the fabricData properties directly
          if (updates.type === 'drawing') {
            updateData.contentJson.fabricData.stroke = updates.contentJson.color;
            updateData.contentJson.fabricData.strokeWidth = updates.contentJson.width || 2;
          } else if (updates.type === 'highlight') {
            updateData.contentJson.fabricData.fill = updates.contentJson.color;
            updateData.contentJson.fabricData.opacity = updates.contentJson.opacity || 0.3;
          } else if (updates.type === 'text') {
            updateData.contentJson.fabricData.fill = updates.contentJson.color;
            updateData.contentJson.fabricData.fontSize = updates.contentJson.fontSize;
            updateData.contentJson.fabricData.text = updates.contentJson.text;
          }
        }

        // Stringify contentJson for API
        updateData.contentJson = JSON.stringify(updateData.contentJson);
      }

      // Update in the store
      await (this.rootStore as any).pdfAnnotationsStore.patch(annotationId, updateData);

      // Reload annotations to ensure sync (with a small delay to ensure store is updated)
      setTimeout(() => {
        this.loadAnnotationsForPage(this.currentPage);
      }, 100);
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message;
      });
    }
  };

  saveCurrentPageAnnotations = () => {
    if (!this.fabricCanvas) return;

    // Get all objects from canvas
    const objects = this.fabricCanvas.getObjects();

    // Save each object as an annotation
    objects.forEach((obj: any) => {
      if (!obj.data?.saved) {
        // Determine annotation type based on fabric object type
        let annotationType = 'drawing'; // Default
        if (obj.type === 'path') {
          annotationType = 'drawing';
        } else if (obj.type === 'rect') {
          annotationType = 'highlight';
        } else if (obj.type === 'i-text' || obj.type === 'textbox') {
          annotationType = 'text';
        }
        
        this.saveAnnotation({
          type: annotationType,
          fabricObject: obj,
          contentJson: {
            type: obj.type,
          },
        });
        obj.data = { ...obj.data, saved: true };
      }
    });
  };

  loadAnnotationsForPage = (pageNumber: number) => {
    if (!this.fabricCanvas) return;

    // Clear existing objects
    this.fabricCanvas.clear();

    // Load annotations for this page
    const annotations = this.currentPageAnnotations;

    annotations.forEach((annotation: any) => {
      // Parse contentJson if it's a string
      let contentJson = annotation.contentJson;
      if (typeof contentJson === 'string') {
        try {
          contentJson = JSON.parse(contentJson);
        } catch (e) {
          console.error('Failed to parse annotation contentJson:', e, annotation);
          return;
        }
      }

      if (contentJson?.fabricData) {
        fabric.util
          .enlivenObjects([contentJson.fabricData])
          .then((objects: fabric.Object[]) => {
            objects.forEach((obj: any) => {
              obj.data = { saved: true, annotationId: annotation.id };
              this.fabricCanvas?.add(obj);
            });
            this.fabricCanvas?.renderAll();
          });
      }
    });
  };

  exportPDF = async () => {
    if (!this.pdfDoc || !this.fabricCanvas) return null;

    // Save current page annotations
    this.saveCurrentPageAnnotations();

    // Create a copy of the PDF
    const pdfCopy = await PDFDocument.create();
    const pages = await pdfCopy.copyPages(
      this.pdfDoc,
      this.pdfDoc.getPageIndices()
    );

    for (const page of pages) {
      pdfCopy.addPage(page);
    }

    // Apply annotations to each page
    const documentIdNumber = this.selectedDocumentId ? parseInt(this.selectedDocumentId as string, 10) : null;
    for (let i = 0; i < this.totalPages; i++) {
      if (!documentIdNumber) continue;
      const pageAnnotations = (this.rootStore as any).pdfAnnotationsStore
        .filterByKeyValue("pageNumber", i + 1)
        .filter((a: any) => a.documentId === documentIdNumber);

      if (pageAnnotations.length > 0) {
        const page = pdfCopy.getPage(i);

        // Convert annotations to PDF drawings
        for (const annotation of pageAnnotations) {
          if (annotation.type === "highlight") {
            const rect = annotation.contentJson.rect;
            page.drawRectangle({
              x: rect.left,
              y: page.getHeight() - rect.top - rect.height,
              width: rect.width,
              height: rect.height,
              color: rgb(1, 1, 0),
              opacity: 0.3,
            });
          } else if (annotation.type === "text") {
            page.drawText(annotation.contentJson.text || "", {
              x: annotation.contentJson.position?.x || 0,
              y: page.getHeight() - (annotation.contentJson.position?.y || 0),
              size: 12,
              color: rgb(0, 0, 0),
            });
          }
        }
      }
    }

    const pdfBytes = await pdfCopy.save();
    return new Blob([pdfBytes], { type: "application/pdf" });
  };

  clearAnnotations = () => {
    if (this.fabricCanvas) {
      this.fabricCanvas.clear();
    }
  };

  // Clean up method for when leaving the route
  cleanup = () => {
    this.loadedDocument = null;
    this.pdfDoc = null;
    this.isLoading = false;
    this.error = null;
    if (this.fabricCanvas) {
      this.fabricCanvas.dispose();
      this.fabricCanvas = null;
    }
    this.pageCache.clear();
  };

  undoLastAnnotation = () => {
    if (!this.fabricCanvas) return;

    const objects = this.fabricCanvas.getObjects();
    if (objects.length > 0) {
      const lastObject = objects[objects.length - 1];
      this.fabricCanvas.remove(lastObject);
    }
  };

  // Collaboration methods
  setupSocketHandlers = () => {
    const pdfEditorStore = this.rootStore.pdfEditorStore;
    if (!pdfEditorStore) return;

    // Handle remote annotation updates
    pdfEditorStore.onAnnotationCreated = (annotation, userId) => {
      if (!this.selectedDocumentId) return;
      const documentIdNumber = parseInt(this.selectedDocumentId as string, 10);
      if (
        annotation.documentId === documentIdNumber &&
        annotation.pageNumber === this.currentPage
      ) {
        this.addRemoteAnnotation(annotation);
      }
    };

    pdfEditorStore.onAnnotationUpdated = (annotation, userId) => {
      if (!this.selectedDocumentId) return;
      const documentIdNumber = parseInt(this.selectedDocumentId as string, 10);
      if (annotation.documentId === documentIdNumber) {
        this.updateRemoteAnnotation(annotation);
      }
    };

    pdfEditorStore.onAnnotationDeleted = (annotationId, userId) => {
      this.removeRemoteAnnotation(annotationId);
    };

    pdfEditorStore.onAnnotationsBatch = (annotations, etag) => {
      this.handleRemoteBatch(annotations, etag);
    };
  };

  enableCollaboration = (documentId: string) => {
    this.isCollaborative = true;

    const pdfEditorStore = this.rootStore.pdfEditorStore;
    if (!pdfEditorStore) return;

    // FeathersJS socket is already connected through the app,
    // just join the document channel
    pdfEditorStore.joinDocument(documentId);
  };

  disableCollaboration = () => {
    if (this.isCollaborative && this.selectedDocumentId) {
      const pdfEditorStore = this.rootStore.pdfEditorStore;
      if (pdfEditorStore) {
        pdfEditorStore.leaveDocument();
      }
    }
    this.isCollaborative = false;
  };

  queueAnnotationForSync = (annotation: any) => {
    this.localAnnotationQueue.push(annotation);
    this.pendingSyncCount = this.localAnnotationQueue.length;

    // Trigger debounced sync
    this.debouncedSync();

    // Send immediate update via Socket.io if collaborative
    if (this.isCollaborative) {
      const pdfEditorStore = this.rootStore.pdfEditorStore;
      if (pdfEditorStore) {
        pdfEditorStore.sendAnnotationUpdate(annotation, "create");
      }
    }
  };

  executeBatchSync = async () => {
    if (this.localAnnotationQueue.length === 0) return;

    const annotations = [...this.localAnnotationQueue];
    this.localAnnotationQueue = [];

    try {
      // Use batch sync with operation type as per backend guide
      if (!this.selectedDocumentId) {
        throw new Error("No document ID available for batch sync");
      }
      const response = await (this.rootStore as any).pdfAnnotationsStore.create(
        {
          operation: "batchSync",
          documentId: parseInt(this.selectedDocumentId as string, 10), // Convert to number
          annotations,
          etag: this.currentEtag,
        }
      );

      // Check if response indicates conflict
      if (response.status === 409 || response.conflict) {
        // Handle conflict
        await this.resolveConflict(
          response.serverAnnotations || response.annotations,
          response.etag || response.newEtag
        );
        return;
      }

      runInAction(() => {
        this.currentEtag = response.etag || response.newEtag;
        this.lastSyncTime = new Date();
        this.pendingSyncCount = 0;
        this.syncError = null;
      });

      // Update local annotations with server response
      if (response.annotations) {
        runInAction(() => {
          // Merge server annotations with local state
          this.loadAnnotationsForPage(this.currentPage);
        });
      }

      // Broadcast batch update if collaborative
      if (this.isCollaborative) {
        const pdfEditorStore = this.rootStore.pdfEditorStore;
        if (pdfEditorStore) {
          pdfEditorStore.sendAnnotationsBatch(annotations, this.currentEtag);
        }
      }
    } catch (error: any) {
      if (error.code === 409 || error.status === 409) {
        // Handle conflict
        await this.resolveConflict();
      } else {
        runInAction(() => {
          this.syncError = error.message;
          // Re-queue failed annotations
          this.localAnnotationQueue = [
            ...annotations,
            ...this.localAnnotationQueue,
          ];
          this.pendingSyncCount = this.localAnnotationQueue.length;
        });
      }
    }
  };

  resolveConflict = async (serverAnnotations?: any[], newEtag?: string) => {
    if (serverAnnotations && newEtag) {
      // Use provided server state
      runInAction(() => {
        this.currentEtag = newEtag;
      });
      // Merge with local changes (server wins for now)
      this.loadAnnotationsForPage(this.currentPage);
    } else {
      // Fetch latest annotations from server
      if (!this.selectedDocumentId) return;
      const latest = await (this.rootStore as any).pdfAnnotationsStore.find({
        query: { documentId: parseInt(this.selectedDocumentId as string, 10) },
      });

      // Merge with local changes (server wins for now)
      this.loadAnnotationsForPage(this.currentPage);
    }

    // Retry sync with updated etag if there are pending changes
    if (this.localAnnotationQueue.length > 0) {
      this.debouncedSync();
    }
  };

  addRemoteAnnotation = (annotation: any) => {
    if (!this.fabricCanvas) return;

    // Check if annotation already exists
    const existing = this.fabricCanvas
      .getObjects()
      .find((obj: any) => obj.data?.annotationId === annotation.id);

    // Parse contentJson if it's a string
    let contentJson = annotation.contentJson;
    if (typeof contentJson === 'string') {
      try {
        contentJson = JSON.parse(contentJson);
      } catch (e) {
        console.error('Failed to parse remote annotation contentJson:', e, annotation);
        return;
      }
    }

    if (!existing && contentJson?.fabricData) {
      fabric.util
        .enlivenObjects([contentJson.fabricData])
        .then((objects: fabric.Object[]) => {
          objects.forEach((obj: any) => {
            obj.data = {
              saved: true,
              annotationId: annotation.id,
              remote: true,
            };
            this.fabricCanvas?.add(obj);
          });
          this.fabricCanvas?.renderAll();
        });
    }
  };

  updateRemoteAnnotation = (annotation: any) => {
    if (!this.fabricCanvas) return;

    const existing = this.fabricCanvas
      .getObjects()
      .find((obj: any) => obj.data?.annotationId === annotation.id);

    if (existing) {
      this.fabricCanvas.remove(existing);
      this.addRemoteAnnotation(annotation);
    }
  };

  removeRemoteAnnotation = (annotationId: string) => {
    if (!this.fabricCanvas) return;

    const obj = this.fabricCanvas
      .getObjects()
      .find((o: any) => o.data?.annotationId === annotationId);

    if (obj) {
      this.fabricCanvas.remove(obj);
      this.fabricCanvas.renderAll();
    }
  };

  handleRemoteBatch = (annotations: any[], etag?: string) => {
    runInAction(() => {
      if (etag) {
        this.currentEtag = etag;
      }
    });

    // Reload annotations for current page
    this.loadAnnotationsForPage(this.currentPage);
  };

  sendCursorPosition = (x: number, y: number) => {
    if (this.isCollaborative) {
      const pdfEditorStore = this.rootStore.pdfEditorStore;
      if (pdfEditorStore) {
        pdfEditorStore.sendCursorPosition(x, y, this.currentPage);
      }
    }
  };

  // Computed values - reference ServiceStore entities
  get selectedDocumentId(): string | null {
    const documentId = this.rootStore.routerStore.params?.documentId;
    return documentId ? String(documentId) : null;
  }

  get currentPage() {
    const pageNumber = this.rootStore.routerStore.queryParams?.pageNumber;
    return pageNumber ? parseInt(String(pageNumber), 10) : 1;
  }

  get currentDocument() {
    // First check if we have a loaded document
    if (this.loadedDocument) {
      return this.loadedDocument;
    }
    
    // Fallback to checking the store
    if (!this.selectedDocumentId) return null;
    return (this.rootStore as any).pdfDocumentsStore.entities.find(
      (d: any) => d.id === this.selectedDocumentId
    );
  }

  get currentPageAnnotations() {
    if (!this.selectedDocumentId) return [];
    const documentIdNumber = parseInt(this.selectedDocumentId as string, 10);
    return (this.rootStore as any).pdfAnnotationsStore
      .filterByKeyValue("pageNumber", this.currentPage)
      .filter((a: any) => a.documentId === documentIdNumber);
  }

  get allAnnotations() {
    if (!this.selectedDocumentId) return [];
    const documentIdNumber = parseInt(this.selectedDocumentId as string, 10);
    return (this.rootStore as any).pdfAnnotationsStore.entities.filter(
      (a: any) => a.documentId === documentIdNumber
    );
  }

  get allDocuments() {
    return (this.rootStore as any).pdfDocumentsStore.entities;
  }

  get collaborators() {
    const pdfEditorStore = this.rootStore.pdfEditorStore;
    return pdfEditorStore ? pdfEditorStore.activeCollaborators : [];
  }

  get isConnected() {
    const pdfEditorStore = this.rootStore.pdfEditorStore;
    return pdfEditorStore ? pdfEditorStore.isConnected : false;
  }
}
